export type WorkHistoryEntry = {
  positionTitle: string;
  organization: string;
  roleDescription: string;
  workLocation: { city: string; state: string };
  startYear: string;
  endYear: string;
  isPresent: boolean;
};

export type WorkHistoryFormValues = {
  workHistory: WorkHistoryEntry[];
};

type PartialWorkHistoryEntry = Partial<WorkHistoryEntry>;

/**
 * Helper to check if a work history entry is empty
 */
export function isEntryEmpty(entry: PartialWorkHistoryEntry | null): boolean {
  if (!entry) {
    return true;
  }

  const stringFields = [
    'positionTitle',
    'organization',
    'roleDescription',
    'startYear',
    'endYear',
  ] as const;

  const hasStringValue = stringFields.some((field) => (entry as any)[field]?.trim());

  const locationFields = ['city', 'state'] as const;

  const hasLocationValue =
    entry.workLocation && locationFields.some((field) => entry.workLocation?.[field]?.trim());

  return !hasStringValue && !hasLocationValue;
}

/**
 * Type guard for work history entries
 */
export function isValidWorkHistoryEntry(entry: unknown): entry is WorkHistoryEntry {
  if (!entry || typeof entry !== 'object') {
    return false;
  }

  const e = entry as PartialWorkHistoryEntry;

  return Boolean(
    typeof e.positionTitle === 'string' &&
    typeof e.organization === 'string' &&
    typeof e.roleDescription === 'string' &&
    e.workLocation &&
    typeof e.workLocation.city === 'string' &&
    typeof e.workLocation.state === 'string' &&
    typeof e.startYear === 'string' &&
    typeof e.endYear === 'string' &&
    (e.isPresent === true || e.isPresent === false)
  );
}

/**
 * Creates an empty work history entry
 */
export function createEmptyEntry(): WorkHistoryEntry {
  return {
    positionTitle: '',
    organization: '',
    roleDescription: '',
    workLocation: { city: '', state: '' },
    startYear: '',
    endYear: '',
    isPresent: false,
  };
}

/**
 * Transform work history entries to API format
 */
export function transformEntriesToApiFormat(entries: WorkHistoryEntry[]) {
  return entries.map(
    ({
      positionTitle,
      organization,
      roleDescription,
      workLocation,
      startYear,
      endYear,
      isPresent,
    }) => ({
      workHistory: {
        positionTitle: positionTitle || '',
        organization: organization || '',
        roleDescription: roleDescription || undefined,
        workLocation:
          workLocation.city || workLocation.state
            ? {
                city: workLocation.city || undefined,
                state: workLocation.state || undefined,
              }
            : undefined,
        startDate: startYear || '',
        endDate: isPresent ? '' : endYear || '',
        isPresent: isPresent || false,
      },
    })
  );
}

export const sortByYear = <
  T extends {
    startYear?: string | null;
    startDate?: string | null;
    endYear?: string | null;
    endDate?: string | null;
    isPresent?: boolean | null;
  },
>(
  entries: T[]
) => {
  return [...entries].sort((a, b) => {
    if (a.isPresent && !b.isPresent) {
      return -1;
    }

    if (!a.isPresent && b.isPresent) {
      return 1;
    }

    const aDate = a.endYear || a.endDate || a.startYear || a.startDate;

    const bDate = b.endYear || b.endDate || b.startYear || b.startDate;

    if (aDate && !bDate) {
      return -1;
    }

    if (!aDate && bDate) {
      return 1;
    }

    if (!aDate && !bDate) {
      return 0;
    }

    const aYear = parseInt(aDate || '0', 10);

    const bYear = parseInt(bDate || '0', 10);

    if (aYear !== bYear) {
      return bYear - aYear;
    }

    const aStartYear = parseInt(a.startYear || a.startDate || '0', 10);

    const bStartYear = parseInt(b.startYear || b.startDate || '0', 10);

    return bStartYear - aStartYear;
  });
};

export const validateYear = (val: string | undefined | null) => {
  if (!val) {
    return true;
  }

  if (typeof val !== 'string' || !/^[0-9]{4}$/.test(val)) {
    return false;
  }

  const year = parseInt(val, 10);

  return year >= 1900 && year <= new Date().getFullYear();
};

export const validateYearRange = (
  startYear: string | undefined,
  endYear: string | undefined,
  isPresent: boolean
) => {
  if (!startYear || !endYear || isPresent) {
    return true;
  }

  return parseInt(endYear, 10) >= parseInt(startYear, 10);
};
